var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useMemo, useRef, useState } from "react";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import { toast } from "sonner";
import { LogType } from "@onvo-ai/js";
import { ArrowsRightLeftIcon, DocumentChartBarIcon, DocumentDuplicateIcon, PencilSquareIcon, PhotoIcon, TrashIcon, } from "@heroicons/react/24/outline";
import ChartBase from "./ChartBase";
import { Card } from "../../tremor/Card";
import { Icon } from "../../tremor/Icon";
import { useDashboard } from "../../layouts/Dashboard/useDashboard";
import { useBackend } from "../../layouts/Wrapper";
import Skeletons from "../Skeletons";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuIconWrapper, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuSubMenu, DropdownMenuSubMenuContent, DropdownMenuSubMenuTrigger, DropdownMenuTrigger, } from "../../tremor/DropdownMenu";
import { useTextWidgetModal } from "../TextWidgetModal";
import { useImageWidgetModal } from "../ImageWidgetModal";
import { useEditWidgetModal } from "../EditWidgetModal";
import { useTheme } from "../../layouts/Dashboard/useTheme";
import { ExclamationTriangleIcon } from "@heroicons/react/16/solid";
import { usePagination } from "./usePagination";
import { PaginationComponent } from "./PaginationComponent";
const DragHandle = () => {
    return (React.createElement("div", { className: "onvo-absolute -onvo-top-6 onvo-w-10 onvo-h-6 onvo-z-10 onvo-hidden group-hover/chartcard:onvo-flex onvo-transition-all onvo-left-[50%] -onvo-ml-5 onvo-justify-center onvo-items-center onvo-foreground-color onvo-border-solid onvo-border onvo-border-b-0 onvo-border-black/5 dark:onvo-border-white/10 onvo-rounded-t-md" },
        React.createElement("div", { className: "onvo-chart-card-drag-handle  onvo-absolute onvo-grid-cols-4 onvo-py-1 onvo-cursor-move onvo-grid onvo-px-1 onvo-items-center onvo-justify-center onvo-h-6 onvo-w-10" },
            React.createElement("div", { className: "onvo-h-[3px] onvo-w-[3px] onvo-ml-0.5 onvo-rounded-full onvo-bg-black/20 dark:onvo-bg-white/20" }),
            React.createElement("div", { className: "onvo-h-[3px] onvo-w-[3px] onvo-ml-0.5 onvo-rounded-full onvo-bg-black/20 dark:onvo-bg-white/20" }),
            React.createElement("div", { className: "onvo-h-[3px] onvo-w-[3px] onvo-ml-0.5 onvo-rounded-full onvo-bg-black/20 dark:onvo-bg-white/20" }),
            React.createElement("div", { className: "onvo-h-[3px] onvo-w-[3px] onvo-ml-0.5 onvo-rounded-full onvo-bg-black/20 dark:onvo-bg-white/20" }),
            React.createElement("div", { className: "onvo-h-[3px] onvo-w-[3px] onvo-ml-0.5 onvo-rounded-full onvo-bg-black/20 dark:onvo-bg-white/20" }),
            React.createElement("div", { className: "onvo-h-[3px] onvo-w-[3px] onvo-ml-0.5 onvo-rounded-full onvo-bg-black/20 dark:onvo-bg-white/20" }),
            React.createElement("div", { className: "onvo-h-[3px] onvo-w-[3px] onvo-ml-0.5 onvo-rounded-full onvo-bg-black/20 dark:onvo-bg-white/20" }),
            React.createElement("div", { className: "onvo-h-[3px] onvo-w-[3px] onvo-ml-0.5 onvo-rounded-full onvo-bg-black/20 dark:onvo-bg-white/20" }))));
};
const ChartCard = ({ widget, className, footer, hideOptions }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    const { dashboard, refreshWidgets, tab, queue } = useDashboard();
    const { setOpen: setEditModalOpen } = useEditWidgetModal();
    const { setOpen: setTextModalOpen } = useTextWidgetModal();
    const { setOpen: setImageModalOpen } = useImageWidgetModal();
    const { backend, adminMode, containerRef } = useBackend();
    const theme = useTheme();
    const cardRef = useRef(null);
    const [cache, setCache] = useState(null);
    // Use pagination store
    const { pagination, setStats, stats, setPagination, resetPagination } = usePagination();
    const duplicate = () => __awaiter(void 0, void 0, void 0, function* () {
        let newObj = Object.assign({}, widget);
        delete newObj.id;
        if (!backend)
            return;
        toast.promise(() => __awaiter(void 0, void 0, void 0, function* () {
            let wid = yield backend.widgets.create(newObj);
            let data = yield backend.widget(wid.id).updateCache();
            return wid;
        }), {
            loading: "Duplicating widget...",
            success: (newWidget) => {
                refreshWidgets(backend);
                backend.logs.create({
                    type: LogType.CreateWidget,
                    dashboard: widget.dashboard,
                    widget: newWidget.id,
                });
                return "Widget duplicated";
            },
            error: (error) => "Error duplicating widget: " + error.message,
        });
    });
    const moveTab = (newTab) => __awaiter(void 0, void 0, void 0, function* () {
        let newObj = Object.assign(Object.assign({}, widget), { tab: newTab });
        if (!backend)
            return;
        toast.promise(() => {
            return backend.widgets.update(widget.id, newObj);
        }, {
            loading: "Moving widget...",
            success: (newWidget) => {
                refreshWidgets(backend);
                return "Widget moved";
            },
            error: (error) => "Error moving widget: " + error.message,
        });
    });
    const deleteWidget = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!backend)
            return;
        toast.promise(() => {
            return backend.widgets.delete(widget.id);
        }, {
            loading: "Deleting widget...",
            success: () => {
                backend.logs.create({
                    type: LogType.DeleteWidget,
                    dashboard: widget.dashboard
                });
                refreshWidgets(backend);
                return "Widget deleted";
            },
            error: (error) => "Error deleting widget: " + error.message,
        });
    });
    const exportWidget = (format) => {
        if (!backend)
            return;
        toast.promise(() => {
            return backend.widget(widget.id).export(format, theme, (event) => {
                console.log("EVENT: ", event);
            });
        }, {
            loading: `Exporting widget as ${format}...`,
            success: (output) => __awaiter(void 0, void 0, void 0, function* () {
                try {
                    // Get the file URL, replacing host.docker.internal with localhost if needed
                    const fileUrl = output.url.replace("host.docker.internal", "localhost");
                    // Fetch the file as a blob
                    const response = yield fetch(fileUrl);
                    if (!response.ok)
                        throw new Error('Network response was not ok');
                    // Get the blob from the response
                    const blob = yield response.blob();
                    // Create a blob URL from the blob
                    const blobUrl = window.URL.createObjectURL(blob);
                    // Create a link element
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = output.fileName || `export.${format}`; // Set the filename
                    link.style.display = 'none';
                    // Add to document, click and remove
                    document.body.appendChild(link);
                    link.click();
                    // Clean up
                    setTimeout(() => {
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(blobUrl); // Release the blob URL
                    }, 100);
                }
                catch (error) {
                    console.error('Download failed:', error);
                    toast.error('Download failed. Please try again.');
                }
                return "Widget exported";
            }),
            error: (error) => "Error exporting widget: " + error.message,
        });
    };
    // Function to fetch paginated data from server
    const fetchPaginatedData = (page, size, column, direction) => __awaiter(void 0, void 0, void 0, function* () {
        if (!backend || !widget || !widget.code || widget.code.trim() === "")
            return;
        try {
            // Fetch paginated data from server
            const { data, pagination: paginationData } = yield queue.add(() => backend.widget(widget.id).cache({
                page,
                pageSize: size,
                sortField: column,
                sortDirection: direction === "DESC" ? "desc" : "asc"
            }));
            setCache(data);
            // Update cache with paginated data
            if (paginationData) {
                // Update pagination metadata
                setStats({
                    totalRows: paginationData.totalRows || 0,
                    totalPages: paginationData.totalPages || 1
                });
            }
        }
        catch (error) {
            console.error("Error fetching paginated data:", error);
        }
    });
    // Handle sort change from pagination store
    const updatePagination = (newPagination) => {
        if (!newPagination)
            return;
        fetchPaginatedData(pagination.currentPage, pagination.pageSize, pagination.sortColumn || undefined, pagination.sortDirection || undefined);
        setPagination(newPagination);
    };
    useEffect(() => {
        if (!widget || !backend)
            return;
        // Function to fetch cache from server
        const fetchServerCache = () => __awaiter(void 0, void 0, void 0, function* () {
            if (!backend || !widget)
                return null;
            try {
                const { data: cacheData } = yield queue.add(() => backend.widget(widget.id).cache());
                // Update component state
                setCache(cacheData);
            }
            catch (error) {
                console.error("Error fetching widget cache from server:", error);
            }
        });
        // Fetch data from server
        fetchServerCache();
        // Reset pagination when widget changes
        return () => {
            resetPagination();
        };
    }, [widget, backend]);
    const layout_editable = adminMode || ((_a = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _a === void 0 ? void 0 : _a.can_edit_widget_layout);
    const widget_editable = adminMode || ((_b = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _b === void 0 ? void 0 : _b.can_edit_widgets);
    const addable = adminMode || ((_c = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _c === void 0 ? void 0 : _c.can_create_widgets);
    const deletable = adminMode || ((_d = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _d === void 0 ? void 0 : _d.can_delete_widgets);
    const ImageDownloadEnabled = useMemo(() => {
        if (adminMode)
            return true;
        if ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) && dashboard.settings.disable_download_images)
            return false;
        return true;
    }, [dashboard, widget, adminMode]);
    const ReportDownloadEnabled = useMemo(() => {
        if (adminMode)
            return true;
        if ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) && dashboard.settings.disable_download_reports)
            return false;
        return true;
    }, [dashboard, widget, adminMode]);
    const error = useMemo(() => {
        if (widget && widget.error && widget_editable) {
            return (React.createElement("div", { onClick: () => setEditModalOpen(true, widget), className: "onvo-absolute onvo-right-4 onvo-bottom-4 onvo-rounded-full onvo-shadow-lg onvo-foreground-color onvo-z-10 onvo-border onvo-border-black/5 dark:onvo-border-white/10" },
                React.createElement("div", { className: "onvo-flex onvo-cursor-pointer onvo-items-center onvo-justify-center onvo-size-10" },
                    React.createElement(ExclamationTriangleIcon, { className: "onvo-text-red-500 onvo-size-5" }))));
        }
        return React.createElement(React.Fragment, null);
    }, [widget]);
    const Skeleton = useMemo(() => Skeletons(widget.type), [widget]);
    const moveDropdown = useMemo(() => {
        if (((dashboard === null || dashboard === void 0 ? void 0 : dashboard.tabs) || []).filter(a => !a.hidden).length <= 1) {
            return React.createElement(React.Fragment, null);
        }
        return (React.createElement(DropdownMenuSubMenu, null,
            React.createElement(DropdownMenuSubMenuTrigger, null,
                React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                    React.createElement(ArrowsRightLeftIcon, { className: "onvo-size-4" }),
                    React.createElement("span", null, "Move widget"))),
            React.createElement(DropdownMenuSubMenuContent, { className: "!onvo-bg-transparent onvo-backdrop-blur-lg" },
                React.createElement("div", { className: "onvo-w-full onvo-absolute onvo-top-0 onvo-left-0 onvo-right-0 onvo-bottom-0 onvo-foreground-color onvo-opacity-50 onvo-rounded-lg -onvo-z-10" }), dashboard === null || dashboard === void 0 ? void 0 :
                dashboard.tabs.map(a => (React.createElement(DropdownMenuItem, { key: a.id, onClick: () => moveTab(a.id) },
                    React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                        React.createElement("span", null, a.label))))))));
    }, [tab, dashboard]);
    const border_radius = useMemo(() => {
        var _a, _b;
        return ((_a = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _a === void 0 ? void 0 : _a.widget_border_radius) === undefined
            ? 16
            : (_b = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _b === void 0 ? void 0 : _b.widget_border_radius;
    }, [dashboard]);
    if (widget.type === "divider") {
        return (React.createElement("div", { ref: cardRef, className: "onvo-group/chartcard onvo-relative onvo-h-full onvo-w-full onvo-py-0 !onvo-bg-transparent !onvo-border-0 !onvo-ring-0 !onvo-shadow-none onvo-px-0 " +
                (className || "") },
            layout_editable && !hideOptions && React.createElement(DragHandle, null),
            deletable && !hideOptions && (React.createElement(DropdownMenu, null,
                React.createElement(DropdownMenuTrigger, { asChild: true },
                    React.createElement(Icon, { className: "!onvo-rounded-full onvo-z-20 group-hover/chartcard:onvo-opacity-100 onvo-transition-all onvo-opacity-0  onvo-cursor-pointer onvo-font-override onvo-absolute onvo-top-1 onvo-right-1", icon: EllipsisVerticalIcon, variant: "shadow" })),
                React.createElement(DropdownMenuContent, { container: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current, className: "!onvo-backdrop-blur-lg onvo-min-w-56 onvo-z-50" },
                    React.createElement("div", { className: "onvo-w-full onvo-absolute onvo-top-0 onvo-left-0 onvo-right-0 onvo-bottom-0 onvo-foreground-color onvo-opacity-50 onvo-rounded-lg -onvo-z-10" }),
                    deletable && (React.createElement(DropdownMenuGroup, null,
                        React.createElement(DropdownMenuItem, { onClick: deleteWidget },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(TrashIcon, { className: "onvo-size-4 onvo-text-red-500" }),
                                React.createElement("span", { className: "onvo-text-red-500" }, "Delete widget")))))))),
            React.createElement(ChartBase, { json: cache, title: "" }),
            footer));
    }
    if (widget.type === "text") {
        let config = widget.config;
        let sub = ((_f = (_e = config === null || config === void 0 ? void 0 : config.options) === null || _e === void 0 ? void 0 : _e.plugins) === null || _f === void 0 ? void 0 : _f.subtitle.text) || "";
        if (typeof sub !== "string") {
            sub = sub.join("<br />");
        }
        return (React.createElement("div", { ref: cardRef, className: "onvo-group/chartcard onvo-relative onvo-h-full onvo-w-full onvo-py-0 !onvo-bg-transparent !onvo-border-0 !onvo-ring-0 !onvo-shadow-none onvo-px-0 " +
                (className || "") },
            layout_editable && !hideOptions && React.createElement(DragHandle, null),
            (widget_editable || deletable) && !hideOptions && (React.createElement(DropdownMenu, null,
                React.createElement(DropdownMenuTrigger, { asChild: true },
                    React.createElement(Icon, { className: "!onvo-rounded-full onvo-z-20 group-hover/chartcard:onvo-opacity-100 onvo-transition-all onvo-opacity-0 onvo-cursor-pointer onvo-font-override onvo-absolute onvo-top-1 onvo-right-1", icon: EllipsisVerticalIcon, variant: "shadow" })),
                React.createElement(DropdownMenuContent, { container: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current, className: "onvo-backdrop-blur-lg onvo-min-w-56 onvo-z-50" },
                    React.createElement("div", { className: "onvo-w-full onvo-absolute onvo-top-0 onvo-left-0 onvo-right-0 onvo-bottom-0 onvo-foreground-color onvo-opacity-50 onvo-rounded-lg -onvo-z-10" }),
                    widget_editable && (React.createElement(DropdownMenuGroup, null,
                        React.createElement(DropdownMenuItem, { onClick: () => {
                                setTimeout(() => {
                                    var _a, _b, _c, _d;
                                    setTextModalOpen(true, {
                                        id: widget.id,
                                        title: widget.title,
                                        subtitle: sub,
                                        prompt: config === null || config === void 0 ? void 0 : config.options.plugins.text.prompt,
                                        titleAlignment: ((_b = (_a = config === null || config === void 0 ? void 0 : config.options.plugins) === null || _a === void 0 ? void 0 : _a.title) === null || _b === void 0 ? void 0 : _b.align) || "start",
                                        descriptionAlignment: ((_d = (_c = config === null || config === void 0 ? void 0 : config.options.plugins) === null || _c === void 0 ? void 0 : _c.subtitle) === null || _d === void 0 ? void 0 : _d.align) || "start",
                                    });
                                }, 30);
                            } },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(PencilSquareIcon, { className: "onvo-size-4" }),
                                React.createElement("span", null, "Edit widget"))),
                        moveDropdown)),
                    widget_editable && deletable && React.createElement(DropdownMenuSeparator, null),
                    deletable && (React.createElement(DropdownMenuGroup, null,
                        React.createElement(DropdownMenuItem, { onClick: deleteWidget },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(TrashIcon, { className: "onvo-size-4 onvo-text-red-500" }),
                                React.createElement("span", { className: "onvo-text-red-500" }, "Delete widget")))))))),
            React.createElement(ChartBase, { json: cache, title: widget.title }),
            footer));
    }
    if (widget.type === "image") {
        return (React.createElement("div", { ref: cardRef, className: `onvo-chart-card onvo-group/chartcard onvo-transition-all onvo-relative onvo-h-full onvo-w-full onvo-py-0 !onvo-bg-transparent !onvo-border-0 !onvo-ring-0 !onvo-shadow-none onvo-px-0 ` +
                (className || ""), style: {
                borderRadius: border_radius + "px"
            } },
            layout_editable && !hideOptions && React.createElement(DragHandle, null),
            (widget_editable || deletable) && !hideOptions && (React.createElement(DropdownMenu, null,
                React.createElement(DropdownMenuTrigger, { asChild: true },
                    React.createElement(Icon, { className: "!onvo-rounded-full onvo-z-20 group-hover/chartcard:onvo-opacity-100 onvo-opacity-0 onvo-cursor-pointer onvo-font-override onvo-absolute onvo-top-1 onvo-right-1", icon: EllipsisVerticalIcon, variant: "shadow" })),
                React.createElement(DropdownMenuContent, { container: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current, className: "onvo-backdrop-blur-lg onvo-min-w-56 onvo-z-50" },
                    React.createElement("div", { className: "onvo-w-full onvo-absolute onvo-top-0 onvo-left-0 onvo-right-0 onvo-bottom-0 onvo-foreground-color onvo-opacity-50 onvo-rounded-lg -onvo-z-10" }),
                    widget_editable && (React.createElement(DropdownMenuGroup, null,
                        React.createElement(DropdownMenuItem, { onClick: () => {
                                setTimeout(() => {
                                    var _a, _b, _c, _d, _e, _f;
                                    setImageModalOpen(true, {
                                        id: widget.id,
                                        url: ((_c = (_b = (_a = widget.config) === null || _a === void 0 ? void 0 : _a.options.plugins) === null || _b === void 0 ? void 0 : _b.image) === null || _c === void 0 ? void 0 : _c.url) || "",
                                        imageFill: ((_f = (_e = (_d = widget.config) === null || _d === void 0 ? void 0 : _d.options.plugins) === null || _e === void 0 ? void 0 : _e.image) === null || _f === void 0 ? void 0 : _f.fill) || "fill",
                                    });
                                }, 30);
                            } },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(PencilSquareIcon, { className: "onvo-size-4" }),
                                React.createElement("span", null, "Edit widget"))),
                        moveDropdown)),
                    widget_editable && deletable && React.createElement(DropdownMenuSeparator, null),
                    deletable && (React.createElement(DropdownMenuGroup, null,
                        React.createElement(DropdownMenuItem, { onClick: deleteWidget },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(TrashIcon, { className: "onvo-size-4 onvo-text-red-500" }),
                                React.createElement("span", { className: "onvo-text-red-500" }, "Delete widget")))))))),
            React.createElement(ChartBase, { json: cache, title: "" }),
            footer));
    }
    const paginationEnabled = widget.engine === "manual-v1" && widget.type === "table";
    return (React.createElement(Card, { ref: cardRef, key: widget.id, id: (_g = widget.settings) === null || _g === void 0 ? void 0 : _g.css_id, className: "onvo-chart-card onvo-h-full onvo-group/chartcard onvo-foreground-color onvo-relative onvo-flex onvo-flex-col !onvo-p-0 " +
            (className || "") +
            " " +
            (((_h = widget.settings) === null || _h === void 0 ? void 0 : _h.css_classnames) ? (_j = widget.settings) === null || _j === void 0 ? void 0 : _j.css_classnames : ""), style: {
            borderRadius: border_radius + "px"
        } },
        error,
        layout_editable && !hideOptions && React.createElement(DragHandle, null),
        (addable ||
            deletable ||
            widget_editable ||
            ImageDownloadEnabled ||
            ReportDownloadEnabled) &&
            !hideOptions && (React.createElement(DropdownMenu, { modal: false },
            React.createElement(DropdownMenuTrigger, { asChild: true },
                React.createElement(Icon, { onClick: (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }, onMouseDown: (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }, onMouseUp: (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }, className: "!onvo-rounded-full onvo-z-20 onvo-transition-all group-hover/chartcard:onvo-opacity-100 onvo-opacity-0 onvo-cursor-pointer !onvo-text-gray-500 onvo-absolute onvo-top-1 onvo-right-1", icon: EllipsisVerticalIcon, variant: "shadow" })),
            React.createElement(DropdownMenuContent, { container: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current, className: "onvo-min-w-56 onvo-backdrop-blur-lg onvo-border-black/10 dark:onvo-border-white/10 onvo-z-50" },
                React.createElement("div", { className: "onvo-w-full onvo-absolute onvo-top-0 onvo-left-0 onvo-right-0 onvo-bottom-0 onvo-foreground-color onvo-opacity-50 onvo-rounded-lg -onvo-z-10" }),
                (addable || widget_editable) && (React.createElement(React.Fragment, null,
                    React.createElement(DropdownMenuLabel, null, "Edit widget"),
                    React.createElement(DropdownMenuGroup, null,
                        widget_editable && (React.createElement(React.Fragment, null,
                            React.createElement(DropdownMenuItem, { onClick: () => {
                                    setEditModalOpen(true, widget);
                                } },
                                React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                    React.createElement(PencilSquareIcon, { className: "onvo-size-4" }),
                                    React.createElement("span", null, "Edit widget"))),
                            moveDropdown)),
                        addable && (React.createElement(DropdownMenuItem, { onClick: duplicate },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(DropdownMenuIconWrapper, null,
                                    React.createElement(DocumentDuplicateIcon, { className: "onvo-size-4" })),
                                React.createElement("span", null, "Duplicate widget"))))))),
                (addable || widget_editable) && ReportDownloadEnabled && (React.createElement(DropdownMenuSeparator, { className: "onvo-border-black/10 dark:onvo-border-white/10" })),
                ReportDownloadEnabled && (React.createElement(React.Fragment, null,
                    React.createElement(DropdownMenuLabel, null, "Download reports"),
                    React.createElement(DropdownMenuGroup, null,
                        React.createElement(DropdownMenuItem, { onClick: () => exportWidget("xlsx") },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(DocumentChartBarIcon, { className: "onvo-size-4" }),
                                React.createElement("span", null, "Download as excel"))),
                        React.createElement(DropdownMenuItem, { onClick: () => exportWidget("csv") },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(DropdownMenuIconWrapper, null,
                                    React.createElement(DocumentChartBarIcon, { className: "onvo-size-4" })),
                                React.createElement("span", null, "Download as CSV")))))),
                (addable || widget_editable || ReportDownloadEnabled) &&
                    ImageDownloadEnabled && React.createElement(DropdownMenuSeparator, { className: "onvo-border-black/10 dark:onvo-border-white/10" }),
                ImageDownloadEnabled && (React.createElement(React.Fragment, null,
                    React.createElement(DropdownMenuLabel, null, "Download images"),
                    React.createElement(DropdownMenuGroup, null,
                        React.createElement(DropdownMenuItem, { onClick: () => exportWidget("svg") },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(PhotoIcon, { className: "onvo-size-4" }),
                                React.createElement("span", null, "Download as SVG"))),
                        React.createElement(DropdownMenuItem, { onClick: () => exportWidget("png") },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(DropdownMenuIconWrapper, null,
                                    React.createElement(PhotoIcon, { className: "onvo-size-4" })),
                                React.createElement("span", null, "Download as PNG"))),
                        React.createElement(DropdownMenuItem, { onClick: () => exportWidget("jpeg") },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(DropdownMenuIconWrapper, null,
                                    React.createElement(PhotoIcon, { className: "onvo-size-4" })),
                                React.createElement("span", null, "Download as JPEG")))))),
                (addable ||
                    widget_editable ||
                    ImageDownloadEnabled ||
                    ReportDownloadEnabled) &&
                    deletable && React.createElement(DropdownMenuSeparator, { className: "onvo-border-black/10 dark:onvo-border-white/10" }),
                deletable && (React.createElement(DropdownMenuGroup, null,
                    React.createElement(DropdownMenuItem, { onClick: deleteWidget },
                        React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                            React.createElement(TrashIcon, { className: "onvo-size-4 onvo-text-red-500" }),
                            React.createElement("span", { className: "onvo-text-red-500" }, "Delete widget")))))))),
        !cache && (React.createElement(Skeleton, null)),
        cache && (React.createElement("div", { className: "onvo-p-0 onvo-flex-grow onvo-w-full onvo-flex onvo-flex-col " + (footer ? "onvo-max-h-[calc(100%-48px)]" : paginationEnabled ? "onvo-max-h-[calc(100%-40px)]" : "onvo-max-h-full") },
            React.createElement(ChartBase, { json: cache, title: widget.title, pagination: pagination, stats: stats, setPagination: updatePagination, padding: (typeof ((_k = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _k === void 0 ? void 0 : _k.widget_padding) === "number") ? (_l = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _l === void 0 ? void 0 : _l.widget_padding : 20 }))),
        footer,
        paginationEnabled && (React.createElement(PaginationComponent, { pagination: pagination, stats: { totalRows: (stats === null || stats === void 0 ? void 0 : stats.totalRows) || 0, totalPages: (stats === null || stats === void 0 ? void 0 : stats.totalPages) || 1 }, setPagination: updatePagination, padding: (typeof ((_m = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _m === void 0 ? void 0 : _m.widget_padding) === "number") ? (_o = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _o === void 0 ? void 0 : _o.widget_padding : 20 }))));
};
export default ChartCard;
//# sourceMappingURL=ChartCard.js.map